import type { Section } from '@containers/Layout/NavMenu/index.util';
import { NavMenuItem } from '@containers/Layout/NavMenu/NavMenuItem';
import { Slide } from '@mui/material';
import {
  Box,
  Nav,
  navCollapsedWidth,
  navExpandedWidth,
  navTransitionDuration,
  navTransitionEasing,
  useTheme,
} from 'hephaestus/web';
import { ArrowBack } from 'hephaestus/webIcons';

export type NavMenuSectionProp = {
  defaultSections: Section[];
  settingsSections: Section[];
  firstAccessibleMenuPath?: string;
  showSettings: boolean;
  pathname: string;
  onNavigate: () => void;
  collapsed?: boolean;
};

const sectionKey = (section: Section) =>
  typeof section.name === 'string' ? section.name : section.name.en;

type NavRailProp = {
  sections: Section[];
  pathname: string;
  onNavigate: () => void;
  collapsed?: boolean;
  hidden: boolean;
  backToPath?: string;
};

type BackToAppItemProp = {
  section: Section;
  pathname: string;
  onNavigate: () => void;
};

const BackToAppItem = ({
  section,
  pathname,
  onNavigate,
}: BackToAppItemProp) => {
  const { color } = useTheme();
  return (
    <NavMenuItem
      section={section}
      pathname={pathname}
      onNavigate={onNavigate}
      sx={{
        mb: 1,
        height: 40,
        minHeight: 40,
        maxHeight: 40,
        boxSizing: 'border-box',
        backgroundColor: color.surfaceContainerVariant,
        color: color.onSurface,
        [`@container nav-rail (min-width: ${navExpandedWidth}px)`]: {
          height: 40,
          minHeight: 40,
          maxHeight: 40,
        },
        '&&:hover, &&[data-preview-state="hover"]': {
          backgroundColor: color.neutralHover,
          color: color.onSurface,
        },
        '&&:active': {
          backgroundColor: color.neutralPressed,
          color: color.onSurface,
        },
      }}
    />
  );
};

const NavRail = ({
  sections,
  pathname,
  onNavigate,
  collapsed,
  hidden,
  backToPath,
}: NavRailProp) => (
  <Box
    aria-hidden={hidden}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      minWidth: 0,
      pointerEvents: hidden ? 'none' : 'auto',
    }}
  >
    <Nav
      collapsed={collapsed}
      aria-label={hidden ? undefined : 'Side rail'}
      sx={{ height: '100%' }}
    >
      {backToPath && (
        <BackToAppItem
          section={{
            name: 'menu.backToApp',
            icon: ArrowBack,
            path: backToPath,
          }}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      )}
      {sections.map((section) => (
        <NavMenuItem
          key={sectionKey(section)}
          section={section}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      ))}
    </Nav>
  </Box>
);

export const NavMenuSection = ({
  defaultSections,
  settingsSections,
  firstAccessibleMenuPath,
  showSettings,
  pathname,
  onNavigate,
  collapsed,
}: NavMenuSectionProp) => {
  const { color, component } = useTheme();
  const navBackground =
    component?.navSection?.backgroundColor ?? color.background;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        flex: '1 1 auto',
        minHeight: 0,
        height: '100%',
        minWidth: 0,
        width: collapsed ? navCollapsedWidth : navExpandedWidth,
        backgroundColor: navBackground,
        transition: `width ${navTransitionDuration}ms ${navTransitionEasing}`,
      }}
    >
      <Slide
        direction='right'
        in={!showSettings}
        appear={false}
        timeout={navTransitionDuration}
        easing={{
          enter: navTransitionEasing,
          exit: navTransitionEasing,
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <NavRail
            sections={defaultSections}
            pathname={pathname}
            onNavigate={onNavigate}
            collapsed={collapsed}
            hidden={showSettings}
          />
        </Box>
      </Slide>
      <Slide
        direction='left'
        in={showSettings}
        appear={false}
        timeout={navTransitionDuration}
        easing={{
          enter: navTransitionEasing,
          exit: navTransitionEasing,
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <NavRail
            sections={settingsSections}
            pathname={pathname}
            onNavigate={onNavigate}
            collapsed={collapsed}
            hidden={!showSettings}
            backToPath={firstAccessibleMenuPath}
          />
        </Box>
      </Slide>
    </Box>
  );
};
